import { useState, useMemo, useEffect, memo } from 'react';
import {
  InputType,
  SmartInput,
  InputTypes,
  InputValidations,
  useTranslation,
  InputAutoComplete,
} from '@nextgen-web-framework/all';
import { isValidPhoneNumber } from 'libphonenumber-js/max';
import { OperationType, UsernameType } from '@commerce-identity-webcomponents/sdk';
import usePhoneNumberData from '../../utils/customHooks/usePhoneNumberData';
import { isEmail, isNumber } from '../../utils/helper';
import useUsernameConfiguration from '../../utils/customHooks/useUsernameConfiguration';
import { InputMode } from '../otp-input-field/types/propTypes';
import { Data, UsernameFieldProps, UsernameFormatType, ValidateEmailPhoneType } from './types/propTypes';
import styles from './UsernameField.module.scss';
const smartInputType = {
  [UsernameFormatType.EMAIL]: InputType.EMAIL,
  [UsernameFormatType.PHONE]: InputType.MOBILE,
  [UsernameFormatType.EMAIL_PHONE]: InputType.EMAIL_MOBILE,
};

const UsernameField = ({
  dropdownLabel,
  error,
  setError,
  defaultLabel,
  className,
  emailLabel,
  mobileLabel,
  onInputChangeCallback,
  forceError,
  clearErrorCallback,
  showErrorCallback,
  checkUsernameConfig,
  autoFocus,
  disabled,
  inputIcon,
  iconClassName,
  onBlur,
  resetValues,
  autoPopulateValue,
  dropDownDisableForSingleCountry = false,
}: UsernameFieldProps) => {
  const t = useTranslation();
  const requireEmailorMobile = t('identity.ErrorMessages.requireEmailOrMobile');
  const requireEmail = t('identity.ErrorMessages.requireEmail');
  const requireCC = t('identity.ErrorMessages.requireCC');
  const requirePhone = t('identity.ErrorMessages.requirePhone');
  const { data, type } = useUsernameConfiguration(checkUsernameConfig as OperationType);
  const { defaultCountryCode } = usePhoneNumberData();
  const { PHONE_INPUT_HIDE_COUNTRY } = ConfigService.getConfigs();
  const [countryCode, setCountryCode] = useState(defaultCountryCode);
  const isStrictlyEmail = useMemo(() => type === UsernameType.EMAIL, [type]);
  const isStrictlyPhone = useMemo(() => type === UsernameType.PHONE, [type]);
  const isSmartInput = useMemo(() => type === UsernameType.EMAIL_PHONE, [type]);
  const [touched, setTouched] = useState<boolean>(false);
  const [emailOrPhoneWithoutCC, setEmailOrPhoneWithoutCC] = useState<string>(autoPopulateValue || '');
  const errorAndTouched = error && touched;
  const status = errorAndTouched || forceError ? InputValidations.INVALID : InputValidations.DEFAULT;
  const message = status === InputValidations.INVALID ? error : '';
  const isValidEmail = ({ emailOrPhoneWithoutCC }: ValidateEmailPhoneType) => {
    if (!isEmail(emailOrPhoneWithoutCC)) {
      showErrorCallback?.(requireEmail);
      return setError(requireEmail);
    } else {
      clearErrorCallback?.();
      return setError('');
    }
  };
  useEffect(() => {
    if (resetValues) {
      setEmailOrPhoneWithoutCC('');
      setCountryCode(defaultCountryCode);
      setTouched(false);
      setError('');
    }
  }, [resetValues, defaultCountryCode, setError]);
  const isValidSmartInputUsername = ({ emailOrPhoneWithoutCC, countryCode }: ValidateEmailPhoneType) => {
    if (touched) {
      if (!emailOrPhoneWithoutCC) {
        showErrorCallback?.(requireEmailorMobile);
        return setError(requireEmailorMobile);
      } else {
        if (isNumber(emailOrPhoneWithoutCC)) {
          return isValidMobile({ emailOrPhoneWithoutCC, countryCode });
        } else {
          return isValidEmail({ emailOrPhoneWithoutCC });
        }
      }
    } else {
      return setError('');
    }
  };

  const isValidMobile = ({ countryCode, emailOrPhoneWithoutCC }: ValidateEmailPhoneType) => {
    if (!isValidPhoneNumber(`${countryCode}${emailOrPhoneWithoutCC}`)) {
      if (touched) {
        if (countryCode) {
          showErrorCallback?.(requirePhone);
          return setError(requirePhone);
        } else {
          showErrorCallback?.(requireCC);
          return setError(requireCC);
        }
      } else {
        return setError('');
      }
    } else {
      clearErrorCallback?.();
      return setError('');
    }
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    clearErrorCallback?.();
    setError('');
    setEmailOrPhoneWithoutCC(e.target.value);
    setTouched(true);
  };

  const onBlurHandler = (value: string) => {
    setTouched(true);
    isStrictlyEmail && isValidEmail({ emailOrPhoneWithoutCC });
    isStrictlyPhone && isValidMobile({ emailOrPhoneWithoutCC, countryCode });
    isSmartInput && isValidSmartInputUsername({ emailOrPhoneWithoutCC, countryCode });

    let cc = '';
    let val = '';

    if (isSmartInput) {
      cc = isNumber(value) ? countryCode : '';
    } else if (isStrictlyPhone) {
      cc = countryCode;
    }

    if (isStrictlyEmail) {
      val = value;
    } else {
      val = `${cc}${value}`;
    }

    if (!value) {
      setError('');
    }

    if (isValidPhoneNumber(val) || isEmail(val)) {
      onBlur && onBlur(val);
    }
  };

  const onSelectHandler = (e: Data) => {
    clearErrorCallback?.();
    setError('');
    setCountryCode(`${e.callingCode}`);
  };

  const configData = useMemo(() => ({ label: 'name', onSelect: onSelectHandler }), []);
  const countryData = useMemo(
    () =>
      data?.map((item) => ({
        id: item.pk,
        name: `(+${item.phoneCode})`,
        callingCode: `+${item.phoneCode}`,
        countryName: item.countryName,
        desc: item?.countryName,
      })),
    [],
  );

  useEffect(() => {
    const cc = isSmartInput ? (isNumber(emailOrPhoneWithoutCC) ? countryCode : '') : isStrictlyPhone ? countryCode : '';
    const val = isStrictlyEmail ? emailOrPhoneWithoutCC : `${cc}${emailOrPhoneWithoutCC}`;
    onInputChangeCallback?.({
      emailOrPhoneWithoutCC,
      countryCode,
      amwayId: val,
    });
  }, [emailOrPhoneWithoutCC, countryCode]);

  let usernameFieldClassName = '';
  if (dropDownDisableForSingleCountry || countryData?.length === 1) {
    usernameFieldClassName = className
      ? `${className} ${styles['username-field-smart-input']}`
      : styles['username-field-smart-input'];
  } else {
    usernameFieldClassName = className ?? '';
  }
  // based on the username, mobile should either show number or text keyboard
  const inputTypeKeyboardBasedOnUsernameType = isStrictlyPhone ? InputMode.NUMERIC : InputMode.TEXT;

  return (
    <SmartInput
      className={usernameFieldClassName}
      data-testid="username_field"
      dropdownLabel={dropDownDisableForSingleCountry || countryData?.length == 1 ? '' : dropdownLabel}
      defaultLabel={defaultLabel}
      mobileLabel={mobileLabel}
      emailLabel={emailLabel}
      dropdownValue={countryCode ? `(${countryCode})` : countryCode}
      smartInputType={smartInputType[type]}
      data={countryData as Data[]}
      configData={configData}
      inputValue={emailOrPhoneWithoutCC}
      type={type === UsernameType.PHONE ? InputTypes.NUMBER : InputTypes.TEXT}
      inputMode={inputTypeKeyboardBasedOnUsernameType}
      status={status}
      message={message}
      onChange={onChange}
      onBlur={(e) => onBlurHandler(e.target.value)}
      disabled={disabled}
      inputIcon={inputIcon}
      iconClassName={iconClassName}
      // eslint-disable-next-line
      autoFocus={autoFocus}
      dropdownDisabled={dropDownDisableForSingleCountry || countryData?.length == 1}
      disabledFilter={true}
      hideDropdown={PHONE_INPUT_HIDE_COUNTRY as boolean}
      autoComplete={InputAutoComplete.OFF}
    />
  );
};
export { UsernameFormatType };

export default memo(UsernameField);
